// App.js - with persistent SideMenu (left) and NavBar (top) for all signed-in views

import React, { useState, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import Constants from './Constants';
import HomeView from './views/HomeView';
import SignupView from './views/SignupView';
import VerificationEmailSentView from './views/VerificationEmailSentView';
import EmailVerifiedView from './views/EmailVerifiedView';
import EmailVerificationInvalidView from './views/EmailVerificationInvalidView';

import SubscriptionPlansView from './views/SubscriptionPlansView';
import PaymentView from './views/PaymentView';
import PaymentSuccessView from './views/PaymentSuccessView';
import PaymentErrorView from './views/PaymentErrorView';
import RequestPasswordResetView from './views/RequestPasswordResetView';
import PasswordResetView from './views/PasswordResetView';
// import LifetimeDealCalculatorView from './views/LTDCalculatorView';
import LoginView from './views/LoginView';
import FeedbackFormView from './views/FeedbackFormView';

import WhisperTestbedView from './views/WhisperTestbedView';
import AgentsView from './views/AgentsView';
import AgentView from './views/AgentView';
import CallsView from './views/CallsView';
import CallView from './views/CallView';
import DashboardView from './views/DashboardView';
import PersonaEditorView from './views/PersonaEditorView';
import UnitEconomicsView from './views/UnitEconomicsView';
import ColdEmailerView from './views/coldemailer/ColdEmailerView';
import EmailCampaignView from './views/coldemailer/EmailCampaignView';
import CampaignEmailView from './views/coldemailer/CampaignEmailView';
import OutreachRecipientView from './views/coldemailer/OutreachRecipientView';
import LeadDiscoveryView from './views/coldemailer/LeadDiscoveryView';
import CompanyView from './views/coldemailer/CompanyView';
import EmailListView from './views/coldemailer/EmailListView';
import BetaTestView from './views/BetaTestView';
import ProfileView from './views/ProfileView';
import HelpView from './views/HelpView';

import SalesProfilesView from './views/SalesProfilesView';
import SalesProfileView from './views/SalesProfileView';

import SideMenu from './components/SideMenu';
import NavBar from './components/NavBar';
import IntroModal from './components/IntroModal';
import { AuthProvider, useAuth, has_permission } from './AuthContext';

import './App.css';
import '@fortawesome/fontawesome-free/css/all.min.css';

function SuperadminRoute({ children }) {
	const { is_superadmin, auth_loading } = useAuth();

	if(auth_loading) {
		return null;
	}

	if(!is_superadmin) {
		return <Navigate to="/dashboard" replace />;
	}

	return children;
}

// The parts of the app a plan pays for. A suspended subscription grants no features, so these are
// closed rather than left to fail deeper in: hiding the menu link alone still lets the page be
// reached by typing the address.
//
// Sent to the account page rather than the dashboard, because that is where the reason is given.
function PlanFeatureRoute({ children }) {
	const { is_superadmin, subscription_features, auth_loading } = useAuth();

	if(auth_loading) {
		return null;
	}

	const has_plan_features = is_superadmin || (subscription_features && subscription_features.length > 0);

	if(!has_plan_features) {
		return <Navigate to="/account" replace />;
	}

	return children;
}

function PersonaEditorRoute({ children }) {
	const { is_superadmin, user_permissions, auth_loading } = useAuth();

	if(auth_loading) {
		return null;
	}

	if(!is_superadmin && !has_permission(user_permissions, 'persona_editor')) {
		return <Navigate to="/dashboard" replace />;
	}

	return children;
}


function AuthenticatedLayout() {
	const { token, app_user_data, set_app_user_data } = useAuth();
	const [intro_dismissed, set_intro_dismissed] = useState(false);

	const handle_intro_dismiss = useCallback(function() {
		set_intro_dismissed(true);
		const user_id = localStorage.getItem(Constants.USER_ID);
		const stored_token = localStorage.getItem(Constants.TOKEN_NAME);
		if(!user_id || !stored_token) {
			return;
		}
		const headers = { Authorization: 'Bearer ' + stored_token };
		axios.put(
			Constants.SERVER_URL + '/api/user/' + user_id + '/app_data',
			{ intro_help_modal_shown: true },
			{ headers }
		).then(function(res) {
			set_app_user_data(res.data);
		}).catch(function() {});
	}, [set_app_user_data]);

	if(!token) {
		return <Navigate to="/login" replace />;
	}

	const show_intro = !intro_dismissed && app_user_data !== null && !app_user_data.intro_help_modal_shown;

	return (
		<div className="App">
			{show_intro ? <IntroModal on_dismiss={handle_intro_dismiss} /> : null}
			<SideMenu />
			<div className="main-content">
				<NavBar />
				<Routes>
					<Route path="/" element={<Navigate to="/dashboard" replace />} />
					<Route path="/dashboard" element={<DashboardView />} />
					<Route path="/feedbackform" element={<FeedbackFormView />} />
					<Route path="/agents" element={<SuperadminRoute><AgentsView /></SuperadminRoute>} />
					<Route path="/agents/:id" element={<SuperadminRoute><AgentView /></SuperadminRoute>} />
					<Route path="/calls" element={<PlanFeatureRoute><CallsView /></PlanFeatureRoute>} />
					<Route path="/call/:session_id" element={<PlanFeatureRoute><CallView /></PlanFeatureRoute>} />
					<Route path="/admin/personas" element={<PersonaEditorRoute><PersonaEditorView /></PersonaEditorRoute>} />
					<Route path="/admin/unit-economics" element={<SuperadminRoute><UnitEconomicsView /></SuperadminRoute>} />
					<Route path="/admin/cold-emailer" element={<SuperadminRoute><ColdEmailerView /></SuperadminRoute>} />
					<Route path="/admin/cold-emailer/email/:id" element={<SuperadminRoute><CampaignEmailView /></SuperadminRoute>} />
					<Route path="/admin/cold-emailer/recipient/:id" element={<SuperadminRoute><OutreachRecipientView /></SuperadminRoute>} />
					<Route path="/admin/cold-emailer/list/:id" element={<SuperadminRoute><EmailListView /></SuperadminRoute>} />
					<Route path="/admin/cold-emailer/:id" element={<SuperadminRoute><EmailCampaignView /></SuperadminRoute>} />
					<Route path="/admin/beta-test" element={<SuperadminRoute><BetaTestView /></SuperadminRoute>} />
					<Route path="/account" element={<ProfileView />} />
					<Route path="/sales-profiles" element={<PlanFeatureRoute><SalesProfilesView /></PlanFeatureRoute>} />
					<Route path="/sales-profiles/:id" element={<PlanFeatureRoute><SalesProfileView /></PlanFeatureRoute>} />
					<Route path="/leaddiscovery" element={<SuperadminRoute><LeadDiscoveryView /></SuperadminRoute>} />
					<Route path="/leaddiscovery/:id" element={<SuperadminRoute><CompanyView /></SuperadminRoute>} />
					<Route path="/help" element={<HelpView />} />
					<Route path="/help/:key" element={<HelpView />} />
				</Routes>
			</div>
		</div>
	);
}




function App() {

	// <Route path="/*" element={<AuthenticatedLayout />} />

	return (
		<AuthProvider>
			<BrowserRouter>
				<Routes>

					<Route path="/*" element={<AuthenticatedLayout />} />

					<Route path="/login" element={<LoginView />} />
					<Route path="/signup" element={<SignupView />} />
					<Route path="/verification_email_sent" element={<VerificationEmailSentView />} />
					<Route path="/email_verified" element={<EmailVerifiedView />} />
					
					<Route path="/email_link_invalid" element={<EmailVerificationInvalidView />} />
					<Route path="/request_password_reset" element={<RequestPasswordResetView />} />
					<Route path="/password_reset/:token" element={<PasswordResetView />} />
					
<Route path="/plans" element={<SubscriptionPlansView />} />
<Route path="/payment" element={<PaymentView />} />
<Route path="/payment_success" element={<PaymentSuccessView />} />
<Route path="/payment_error" element={<PaymentErrorView />} />
					
					<Route path="/whisper-test" element={<WhisperTestbedView />} />

					

				</Routes>
			</BrowserRouter>
		</AuthProvider>
	);
}



export default App;
